import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang, formatCurrency } from "../lib/providers";
import { TextInput, Select, Btn } from "./primitives";
import { MagnifyingGlass, X, CaretDown, CaretRight, Plus, FunnelSimple } from "@phosphor-icons/react";

/**
 * Modal to pick one or more positions from the catalog, grouped by category, with
 * search + category filter + unit filter. Calls onPick(position) per selection.
 */
export default function CatalogPicker({ open, onClose, onPick }) {
  const { t, lang } = useLang();
  const [positions, setPositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCats, setFilterCats] = useState([]);
  const [filterUnit, setFilterUnit] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([api.get("/positions"), api.get("/categories")])
      .then(([p, c]) => { setPositions(p.data); setCategories(c.data); })
      .finally(() => setLoading(false));
  }, [open]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const filtered = useMemo(() => {
    let list = [...positions];
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(qq) ||
        (p.description || "").toLowerCase().includes(qq) ||
        (p.number || "").toLowerCase().includes(qq)
      );
    }
    if (filterCats.length) list = list.filter((p) => filterCats.includes(p.category_id || "__none"));
    if (filterUnit) list = list.filter((p) => p.unit === filterUnit);
    list.sort((a, b) => (a.number || "").localeCompare(b.number || ""));
    return list;
  }, [positions, q, filterCats, filterUnit]);

  const grouped = useMemo(() => {
    const g = new Map();
    filtered.forEach((p) => {
      const key = p.category_id || "__none";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(p);
    });
    return Array.from(g.entries()).sort(([a], [b]) => (catMap[a]?.number ?? 99) - (catMap[b]?.number ?? 99));
  }, [filtered, catMap]);

  const toggleCat = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleFilterCat = (id) => setFilterCats((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);

  const units = Array.from(new Set(positions.map((p) => p.unit))).filter(Boolean);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-6 z-50 overflow-y-auto" onClick={onClose}>
      <div className="swiss-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col mt-6" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">{t("pickPosition")}</h3>
          <button onClick={onClose} data-testid="catalog-picker-close" className="text-[var(--muted)] hover:text-[var(--ink)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-[var(--border)] space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
              <TextInput data-testid="picker-search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" autoFocus />
            </div>
            <Btn variant="secondary" onClick={() => setFiltersOpen(!filtersOpen)} className="h-10">
              <FunnelSimple size={14} /> {t("filters")}
              {(filterCats.length + (filterUnit ? 1 : 0)) > 0 && (
                <span className="ml-1 px-1.5 text-xs bg-[var(--brand)] text-white rounded-sm">
                  {filterCats.length + (filterUnit ? 1 : 0)}
                </span>
              )}
            </Btn>
          </div>
          {filtersOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div>
                <div className="label-overline mb-2">{t("category")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((c) => {
                    const active = filterCats.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggleFilterCat(c.id)}
                        className={`px-2 py-1 text-xs rounded-sm border transition-colors ${active ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"}`}>
                        {String(c.number || "?").padStart(2, "0")} {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="label-overline mb-2">{t("unit")}</div>
                <Select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)}>
                  <option value="">{t("allUnits")}</option>
                  {units.map((u) => <option key={u} value={u}>{u}</option>)}
                </Select>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-[var(--muted)]">…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[var(--muted)] text-sm">
              {positions.length === 0 ? (
                <>{lang === "de" ? "Dein Katalog ist noch leer. " : "Your catalog is empty. "}<Link to="/positions" className="text-[var(--brand)] hover:underline">{t("newPosition")}</Link></>
              ) : (
                lang === "de" ? "Keine Treffer." : "No results."
              )}
            </div>
          ) : (
            <div className="divide-y divide-[var(--border)]">
              {grouped.map(([catKey, items]) => {
                const cat = catMap[catKey];
                const isCollapsed = collapsed.has(catKey);
                return (
                  <div key={catKey}>
                    <button onClick={() => toggleCat(catKey)}
                      className="w-full flex items-center gap-3 px-5 py-2.5 bg-[var(--surface-alt)] hover:bg-[var(--row-hover)] transition-colors text-left border-b border-[var(--border)]">
                      {isCollapsed ? <CaretRight size={14} /> : <CaretDown size={14} />}
                      <span className="font-mono text-xs px-2 py-0.5 bg-[var(--brand-soft)] text-[var(--brand)] rounded-sm">
                        {String(cat?.number || "??").padStart(2, "0")}
                      </span>
                      <span className="font-medium text-[var(--ink)]">{cat?.name || t("uncategorized")}</span>
                      <span className="text-xs text-[var(--muted-2)] ml-auto">{items.length}</span>
                    </button>
                    {!isCollapsed && items.map((p) => (
                      <button key={p.id} onClick={() => onPick(p)} data-testid={`picker-pick-${p.id}`}
                        className="w-full text-left px-5 py-3 hover:bg-[var(--row-hover)] flex items-center justify-between gap-4 border-b border-[var(--border)]/50">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-[var(--muted)]">{p.number}</span>
                            <span className="font-medium truncate">{p.name}</span>
                          </div>
                          {p.description && <div className="text-xs text-[var(--muted-2)] truncate mt-0.5">{p.description}</div>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs font-mono text-[var(--muted)]">{p.unit}</span>
                          <span className="text-sm font-medium">{formatCurrency(p.unit_price, lang)}</span>
                          <Plus size={14} className="text-[var(--brand)]" />
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
